import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { FunctionalLoader, IntentBehaviorProvider, useDidUpdate, useOverlayContext } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { WithdrawnForEditingPage } from '~/components/WithdrawnForEditingPage/WithdrawnForEditingPage';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { getIntentBehavior } from '~/infra/access-control';
import { relationshipModel } from '~/models';
import { requestAccountantAccessModel } from '~/models/request-accountant-access';
import { BIRelationshipScopeProvider } from '~/providers/BIRelationshipScopeProvider';
import { selectors } from '~/store/state-selectors';
import { makeDefaultBranding } from '~/store/store.utils';
import { COBStep, PreviewState } from './ClientOnboarding.utils';
import { ClientOnboardingInner } from './ClientOnboardingInner/ClientOnboardingInner';
import { CobBrandingProvider } from './CobBrandingContext';
import { CobStorageProvider, useCobStorage } from './CobStorageContext';
import { changeBusinessLoader } from './WelcomeStep/welcomeStepUtils';
export function ClientOnboarding(props) {
    const userRole = useStore(selectors.user.role);
    const userId = useStore(selectors.user.id);
    const { ENABLE_KILL_ACCOUNTANT_ACCESS_FE } = useFeatureFlags();
    const isViewModeFromStore = useStore(selectors.isViewMode);
    const isViewMode = !!ENABLE_KILL_ACCOUNTANT_ACCESS_FE && isViewModeFromStore;
    const intentBehavior = useMemo(()=>getIntentBehavior(userRole, {
            relationshipState: {
                relationship: props.relationship,
                isPreviewMode: !!props.previewState
            },
            userId,
            isViewMode
        }), [
        userRole,
        props.relationship,
        props.previewState,
        userId,
        isViewMode
    ]);
    const relationshipScope = {
        id: props.relationship.id,
        isApproved: relationshipModel.isApproved(props.relationship)
    };
    return /*#__PURE__*/ _jsx(BIRelationshipScopeProvider, {
        relationshipScope: relationshipScope,
        children: /*#__PURE__*/ _jsx(IntentBehaviorProvider, {
            value: intentBehavior,
            children: /*#__PURE__*/ _jsx(ClientOnboardingInner, _object_spread({}, props))
        })
    });
}
export function ClientOnboardingRouted(props) {
    const { relationship } = props;
    const ff = useStore(selectors.ff.featureFlags);
    const businessId = useStore(selectors.user.businessId);
    const ffRelationshipVendorId = useStore((state)=>state.ff.relationshipVendorId);
    const { ff: { setRelationshipVendorId } } = useStoreActions();
    const { getFromStorage } = useCobStorage(relationship);
    const fetchingFF = !ff.data || !ffRelationshipVendorId;
    const relationshipVendorId = relationshipModel.vendorID(relationship);
    useEffect(()=>{
        setRelationshipVendorId(relationshipVendorId);
        return ()=>{
            setRelationshipVendorId();
        };
    }, [
        relationshipVendorId
    ]);
    if (fetchingFF) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            name: 'fetchFF',
            show: true
        });
    }
    return /*#__PURE__*/ _jsxs(Switch, {
        children: [
            /*#__PURE__*/ _jsx(Route, {
                path: "".concat(routes.clientOnboarding(), "/:stepId"),
                render: (param)=>{
                    let { history, match } = param;
                    const vendorBranding = relationship.vendor.branding;
                    const brandingDefaults = makeDefaultBranding();
                    const initialBranding = _object_spread_props(_object_spread({}, vendorBranding), {
                        progressBarComplete: vendorBranding.buttonPrimary,
                        buttonSecondary: brandingDefaults.buttonSecondary,
                        buttonDisabled: brandingDefaults.buttonDisabled
                    });
                    return /*#__PURE__*/ _jsx(CobBrandingProvider, {
                        initialBranding: initialBranding,
                        children: /*#__PURE__*/ _jsx(ClientOnboarding, {
                            previewState: // A vendor can enter here by copying the proposal-link and pasting it in a new tab while logged in.
                            relationship && relationship.vendor.id === businessId && !relationship.isSample ? PreviewState.Preview : undefined,
                            refresh: props.refresh,
                            relationship: relationship,
                            currentStep: match.params.stepId,
                            setCurrentStep: (step)=>history.push(routes.clientOnboarding(relationshipModel.id(relationship), step)),
                            entityContext: {
                                source: 'relationship',
                                id: relationshipModel.id(relationship)
                            }
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.clientOnboarding(),
                children: (param)=>{
                    let { match } = param;
                    var _getFromStorage;
                    return /*#__PURE__*/ _jsx(Redirect, {
                        to: "".concat(match.url, "/").concat((_getFromStorage = getFromStorage('last_step')) !== null && _getFromStorage !== void 0 ? _getFromStorage : COBStep.welcome)
                    });
                }
            })
        ]
    });
}
export function ClientOnboardingQueryWrapper(props) {
    const { relationshipId } = props.match.params;
    const businessId = useStore(selectors.user.businessId);
    const { api } = useStoreActions();
    const overlayContext = useOverlayContext();
    useDidUpdate(()=>{
        overlayContext.stopLoader(changeBusinessLoader);
    }, [
        businessId
    ]);
    async function fetchData() {
        const [relationship, membersResponse] = await Promise.all([
            api.relationship.get(relationshipId),
            api.member.getAll()
        ]);
        const shouldSetAccountants = requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess);
        return {
            relationship,
            accountants: shouldSetAccountants ? membersResponse.accountants : []
        };
    }
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetchData,
        renderChild: (param)=>{
            let { data, refresh } = param;
            if (data.relationship.withdrawnForEditing) {
                return /*#__PURE__*/ _jsx(WithdrawnForEditingPage, {
                    vendorName: relationshipModel.vendorName(data.relationship),
                    onRefresh: refresh
                });
            }
            return /*#__PURE__*/ _jsx(ClientOnboardingRouted, {
                relationship: data.relationship,
                refresh: refresh
            });
        }
    });
}
export default function ClientOnboardingContextWrapper(props) {
    return /*#__PURE__*/ _jsx(CobStorageProvider, {
        children: /*#__PURE__*/ _jsx(ClientOnboardingQueryWrapper, _object_spread({}, props))
    });
}
